import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64url public key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotifications = () => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
      setSupported(true);
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch (e) {
    }
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Necesitamos tu permiso para enviarte notificaciones');
        return;
      }

      // Get VAPID public key
      const response = await axios.get(`${API}/notifications/vapid-public-key`);
      const vapidKey = response.data.public_key;

      // Subscribe via service worker
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      const subJSON = subscription.toJSON();
      await axios.post(
        `${API}/notifications/subscribe`,
        { endpoint: subJSON.endpoint, keys: subJSON.keys },
        {
          withCredentials: true
        }
      );

      setSubscribed(true);
      toast.success('¡Notificaciones activadas!');
    } catch (error) {
      toast.error('Error al activar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const subJSON = subscription.toJSON();
        await axios.post(
          `${API}/notifications/unsubscribe`,
          { endpoint: subJSON.endpoint, keys: subJSON.keys },
          {
            withCredentials: true
          }
        );
        await subscription.unsubscribe();
      }

      setSubscribed(false);
      toast.success('Notificaciones desactivadas');
    } catch (error) {
      toast.error('Error al desactivar');
    } finally {
      setLoading(false);
    }
  };

  if (!supported) {
    return (
      <Card className="bg-white/50 border-[#E5E5E5]" data-testid="push-notifications-unsupported">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-[#737373]">
            <BellOff className="w-5 h-5" />
            <p className="text-sm">Tu navegador no soporta notificaciones push</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/50 border-[#E5E5E5]" data-testid="push-notifications-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0A0A0A]">
          {subscribed ? <BellRing className="w-6 h-6 text-primary" /> : <Bell className="w-6 h-6 text-[#737373]" />}
          Notificaciones Push
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#525252]">
          Recibí avisos sobre nuevos eventos, recordatorios de RSVPs y novedades de Mi Tribu directamente en tu dispositivo.
        </p>

        {permission === 'denied' && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-300 text-sm">
              Las notificaciones fueron bloqueadas. Habilitalas desde la configuración de tu navegador.
            </p>
          </div>
        )}

        {subscribed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <BellRing className="w-5 h-5 text-green-400" />
              <p className="text-green-300 text-sm">Notificaciones activadas en este dispositivo</p>
            </div>
            <Button
              onClick={unsubscribe}
              disabled={loading}
              variant="outline"
              className="border-[#E5E5E5] text-[#525252]"
              data-testid="unsubscribe-push-button"
            >
              <BellOff className="w-4 h-4 mr-2" />
              {loading ? 'Desactivando...' : 'Desactivar notificaciones'}
            </Button>
          </div>
        ) : (
          <Button
            onClick={subscribe}
            disabled={loading || permission === 'denied'}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="subscribe-push-button"
          >
            <Bell className="w-4 h-4 mr-2" />
            {loading ? 'Activando...' : 'Activar notificaciones'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotifications;
